import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../components/ui/popover";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../../components/ui/select";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../../components/ui/command";
import { Checkbox } from "../../components/ui/checkbox";
import { ChevronsUpDown, Check } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import toast from "react-hot-toast";
import apiClient from "../../utils/apiClient";
import server from "../../utils/serverConfig";

const orderTypes = ["MARKET", "LIMIT", "STOP", "STOP-LIMIT"];
const sideOptions = [
  { value: "1", label: "Buy" },
  { value: "2", label: "Sell" },
];

const strategyOptions = [
  "HE_PC5_MA10",
  "HE_PC2_MA10",
  "HHPC_PC2_MA10",
  "HHPC_PC5_MA10",
  "PDI_MDI_PC2_MA10",
  "PDI_MDI_PC5_MA10",
  "HE_PC5",
  "HE_PC2",
  "PDI_MA_PC2",
  "PDI_MA_PC5",
  "PDI_MDI_PC2",
  "PDI_MDI_PC5",
  "PDI_MA_PC2_MA10",
  "PDI_MA_PC5_MA10",
  "HHPC_PC2",
  "HHPC_PC5",
  "ORB_1",
];

const DhanSSOrderPlacement = ({
  userLoading,
  filteredData,
  selectedClients,
  setSelectedClients,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [symbols, setSymbols] = useState([]);
  const [allSymbols, setAllSymbols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [symbolText, setSymbolText] = useState("");
  const [orderType, setOrderType] = useState("");
  const [side, setSide] = useState("");
  const [stopPrice, setStopPrice] = useState(0);
  const [limitPrice, setLimitPrice] = useState(0);
  const [qty, setQty] = useState(0);
  const [openClients, setOpenClients] = useState(false);
  const [strategyText, setStrategyText] = useState("");
  const [strategyDropdown, setStrategyDropdown] = useState("");
  const [multiplier, setMultiplier] = useState(false);

  // Fetch symbols
  const fetchSymbols = async (term) => {
    setLoading(true);
    try {
      const response = await apiClient.get("get_symticker");
      const data = response.data;
      setAllSymbols(data);
      const filteredSymbols = data
        .filter((symbol) => symbol.toLowerCase().includes(term.toLowerCase()))
        .slice(0, 10);
      setSymbols(filteredSymbols);
      setHasFetched(true);
    } catch (error) {
      console.error("Error fetching symbols:", error);
      setSymbols([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 3) {
        if (!hasFetched) {
          fetchSymbols(searchTerm);
        } else {
          const filteredSymbols = allSymbols
            .filter((symbol) =>
              symbol.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .slice(0, 10);
          setSymbols(filteredSymbols);
        }
      } else {
        setSymbols([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, hasFetched, allSymbols]);

  // Handle symbol selection
  const handleSymbolSelect = (symbol) => {
    setSelectedSymbol(symbol);
    setSearchTerm(symbol);
    setOpen(false);
  };

  // Handle manual input submission
  const handleInputSubmit = () => {
    if (searchTerm && searchTerm !== selectedSymbol) {
      setSelectedSymbol(searchTerm);
      setOpen(false);
    }
  };

  const handleOrderPlacement = () => {
    let finalSymbol = "";
    let finalStrategy = "";

    // Step 1: Prioritize dropdown
    if (selectedSymbol && selectedSymbol.trim() !== "") {
      finalSymbol = selectedSymbol.trim();
    } else if (symbolText && symbolText.trim() !== "") {
      finalSymbol = symbolText.trim();
    } else {
      toast.error("Please enter or select a symbol before placing the order.");
      return;
    }

    // Step 2: Prioritize strategy
    if (strategyText && strategyText.trim() !== "") {
      finalStrategy = strategyText.trim();
    } else if (strategyDropdown && strategyDropdown.trim() !== "") {
      finalStrategy = strategyDropdown.trim();
    } else {
      toast.error(
        "Please enter or select a strategy before placing the order."
      );
      return;
    }

    const selectedClientIds = Object.entries(selectedClients)
      .filter(([_, isSelected]) => isSelected)
      .map(([clientId]) => clientId);

    // Step 2: Prepare order payload
    const orderPayload = {
      symbol: finalSymbol,
      strategy: finalStrategy,
      multiplier_wise: multiplier,
      qty: parseInt(qty),
      side: parseInt(side),
      orderType: orderType,
      stopPrice: parseFloat(stopPrice),
      limitPrice: parseFloat(limitPrice),
      clients: selectedClientIds,
    };

    //   // Step 3: Console log the final data
    console.log("📝 Order Payload:", orderPayload);

    apiClient.post("https://optimus.dhanss.server.sypto.xyz/order-placement", orderPayload)
      .then((response) => {
        console.log("Server response:", response.data);
        if (response.data.status === 200) {
          toast.success("Order placed successfully!");
        } else {
          toast.error("Failed to place order. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Failed to place order for Dhan SS Clients", error);
      })
      .finally(() => {
        console.log("Order Placed sucessfully !!");
      });
  };

  const handleResetForm = () => {
    setSelectedSymbol("");
    setSearchTerm("");
    setSymbolText("");
    setOrderType("");
    setSide("");
    setStopPrice(0);
    setLimitPrice(0);
    setQty(0);
    setStrategyDropdown("");
    setStrategyText("");
    setMultiplier(false);
  };

  return (
    <div className="flex flex-col gap-4 w-full mx-auto px-[30px] py-[20px]">
      <div>
        <p className="flex items-center text-lg font-semibold text-white">
          Dhan SS Order Placement
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 py-1 max-w-[600px]">
        <div className="flex items-center space-x-5 w-full">
          {/* Symbol Dropdown */}
          <div className="w-full flex flex-col  gap-y-3">
            <Label className="text-white">Symbol</Label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between text-white"
                >
                  {selectedSymbol || "Search symbol..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="!w-[300px] p-0">
                <Command>
                  <CommandInput
                    placeholder="Search symbol..."
                    value={searchTerm}
                    onValueChange={setSearchTerm}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleInputSubmit();
                      }
                    }}
                  />
                  {loading && (
                    <div className="py-6 text-center text-sm">Loading...</div>
                  )}
                  <CommandEmpty>
                    {searchTerm.length < 3
                      ? "Type at least 3 characters"
                      : "No matching symbols found - press Enter to try anyway"}
                  </CommandEmpty>
                  <CommandList>
                    <CommandGroup className="max-h-[200px] overflow-y-auto scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
                      {symbols.map((symbol) => (
                        <CommandItem
                          key={symbol}
                          value={symbol}
                          onSelect={() => handleSymbolSelect(symbol)}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              selectedSymbol === symbol
                                ? "opacity-100"
                                : "opacity-0"
                            }`}
                          />
                          {symbol}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Symbol Text Input */}
          <div className="w-full grid grid-cols-1 gap-2">
            <Label className="text-white">Symbol Text</Label>
            <Input
              className="text-white"
              value={symbolText}
              onChange={(e) => setSymbolText(e.target.value)}
              placeholder="Enter symbol manually if not searchable"
            />
          </div>
        </div>

        {/* Order Type Dropdown */}
        <div className="flex flex-col  gap-y-3">
          <Label className="text-white">Order Type</Label>
          <Select value={orderType} onValueChange={(val) => setOrderType(val)}>
            <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
              <SelectValue placeholder="Select Order Type" />
            </SelectTrigger>
            <SelectContent className="border-zinc-700">
              <SelectGroup>
                {orderTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Side Dropdown */}
        <div className="flex flex-col  gap-y-3">
          <Label className="text-white">Side</Label>
          <Select value={side} onValueChange={(val) => setSide(val)}>
            <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
              <SelectValue placeholder="Buy / Sell" />
            </SelectTrigger>
            <SelectContent className="border-zinc-700">
              <SelectGroup>
                {sideOptions.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {orderType !== "MARKET" && (
          <div className="flex items-center space-x-5 w-full">
            {/* Stop Price - show unless order type is "Limit" */}
            {(orderType === "STOP" || orderType === "STOP-LIMIT") && (
              <div className="w-full grid grid-cols-1 gap-2">
                <Label className="text-white">Stop Price</Label>
                <Input
                  type="number"
                  className="text-white"
                  value={stopPrice}
                  onChange={(e) => setStopPrice(e.target.value)}
                />
              </div>
            )}

            {/* Limit Price - show unless order type is "Stop" */}
            {(orderType === "LIMIT" || orderType === "STOP-LIMIT") && (
              <div className="w-full grid grid-cols-1 gap-2">
                <Label className="text-white">Limit Price</Label>
                <Input
                  type="number"
                  className="text-white"
                  value={limitPrice}
                  onChange={(e) => setLimitPrice(e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        <div className="flex items-center space-x-5 w-full">
          {/* Quantity */}
          <div className="w-full grid grid-cols-1 gap-2">
            <Label className="text-white">Quantity</Label>
            <Input
              type="number"
              className="text-white"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>
          {/* Multiplier Toggle */}
          <div className="w-full grid grid-cols-1 gap-2">
            <Label className="text-white">Multiplier Wise</Label>
            <div className="flex items-center space-x-2">
              <Switch
                id="multiplier-toggle"
                checked={multiplier}
                onCheckedChange={setMultiplier}
              />
              <Label htmlFor="multiplier-toggle" className="text-white text-sm">
                {multiplier ? "Enabled" : "Disabled"}
              </Label>
            </div>
          </div>
        </div>

        {/* Clients Popover */}
        <div className=" flex flex-col  gap-y-3 ">
          <Label className="text-white">Select Clients</Label>

          <Popover open={openClients} onOpenChange={setOpenClients}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className=" justify-between text-white text-[15px]"
              >
                Select Clients
                <ChevronsUpDown className="opacity-80 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[350px] flex flex-col space-y-2 h-[200px] overflow-y-auto  p-2 border-zinc-800">
              {userLoading ? (
                <div className="flex items-center justify-center">
                  loading clients ...
                </div>
              ) : (
                <>
                  {/* Select All Checkbox */}
                  <div className="flex items-center space-x-2 text-white  py-1 ">
                    <Checkbox
                      id="select-all"
                      className="border-zinc-500 border-[1px]"
                      checked={
                        filteredData.length > 0 &&
                        filteredData.every(
                          (client) => selectedClients[client.client_id]
                        )
                      }
                      onCheckedChange={(checked) => {
                        const newSelection = Object.fromEntries(
                          filteredData.map((client) => [
                            client.client_id,
                            checked === true,
                          ])
                        );
                        setSelectedClients(newSelection);
                      }}
                    />
                    <label
                      htmlFor="select-all"
                      className="text-white font-medium text-[15px]"
                    >
                      Select All Clients
                    </label>
                  </div>

                  {filteredData.map((client) => (
                    <div
                      key={client.client_id}
                      className="flex items-center space-x-2 text-white"
                    >
                      <Checkbox
                        id={client.client_id}
                        checked={selectedClients[client.client_id]}
                        onCheckedChange={() =>
                          setSelectedClients((prev) => ({
                            ...prev,
                            [client.client_id]: !prev[client.client_id],
                          }))
                        }
                      />
                      <label htmlFor={client.client_id}>
                        {client.client_id} ({client.name})
                      </label>
                    </div>
                  ))}
                </>
              )}
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center space-x-5 w-full">
          {/* Strategy Text Input */}
          <div className="w-full grid grid-cols-1 gap-2">
            <Label className="text-white">Strategy Text</Label>
            <Input
              className="text-white"
              value={strategyText}
              onChange={(e) => setStrategyText(e.target.value)}
              placeholder="Enter strategy manually"
            />
          </div>

          {/* Strategy Dropdown */}
          <div className="w-full flex flex-col gap-y-3">
            <Label className="text-white">Strategy Dropdown</Label>
            <Select
              value={strategyDropdown}
              onValueChange={(val) => setStrategyDropdown(val)}
            >
              <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                <SelectValue placeholder="Select Strategy" />
              </SelectTrigger>
              <SelectContent className="border-zinc-700">
                <SelectGroup>
                  {strategyOptions.map((strategy) => (
                    <SelectItem key={strategy} value={strategy}>
                      {strategy}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center py-4 space-x-5 max-w-[600px]">
        <Button
          className="w-[180px] text-[16px] bg-zinc-900 text-white font-poppins font-semibold hover:bg-zinc-800"
          onClick={handleResetForm}
        >
          Reset
        </Button>
        <Button
          className="w-[180px] text-[16px] bg-[#07F2C7]/80 text-black font-poppins font-semibold hover:bg-[#07F2C7] hover:text-black"
          onClick={handleOrderPlacement}
        >
          Place Order
        </Button>
      </div>
    </div>
  );
};

export default DhanSSOrderPlacement;
